import { useRegistryDevicesStore } from "@/composables/useRegistryDevicesStore";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import { deviceGroupsApi } from "@/dashboard/production_strategy/device_groups/deviceGroupsApi";
import type {
  DeviceGroup,
  DeviceGroupCreateRequest,
  DeviceGroupUpdateRequest,
} from "@/dashboard/production_strategy/device_groups/deviceGroupsTypes";
import { defineStore } from "pinia";
import { ref } from "vue";
import logger from "@/logger";
import { apiErrorLogFields } from "@/utils/apiErrors";

/**
 * Device groups per company. Every mutation refetches the company's groups and, when the registry
 * devices store holds that company, its devices too, so membership (`device_group_id`) always comes
 * from the backend. Loaded portfolio items of that company are refetched in the background, since
 * their rows carry the group name too. A failed request propagates to the caller, which knows how
 * to word it; a failed refetch after a committed change does not.
 */
export const useDeviceGroupsStore = defineStore("deviceGroups", () => {
  const groupsByCompany = ref<Record<number, DeviceGroup[]>>({});
  const latestFetchByCompany = new Map<number, number>();

  /** The company's groups ordered by name; empty until fetched. */
  const groupsFor = (companyId: number): DeviceGroup[] =>
    groupsByCompany.value[companyId] ?? [];

  // Only the company's latest request is kept: a load that started before a change may answer last.
  const fetchGroups = async (companyId: number): Promise<void> => {
    const request = (latestFetchByCompany.get(companyId) ?? 0) + 1;
    latestFetchByCompany.set(companyId, request);
    const response = await deviceGroupsApi.list(companyId);
    if (latestFetchByCompany.get(companyId) !== request) {
      return;
    }
    groupsByCompany.value = {
      ...groupsByCompany.value,
      [companyId]: response.data.device_groups,
    };
  };

  // A failed refetch is only logged: the change itself is committed, and rejecting would invite the
  // caller to repeat it. The stale groups or devices stay until their next load.
  const refreshCompany = async (companyId: number): Promise<void> => {
    const registryDevicesStore = useRegistryDevicesStore();
    await Promise.all([
      fetchGroups(companyId),
      registryDevicesStore.currentCompanyId === companyId
        ? registryDevicesStore.refreshRegistryDevices(companyId)
        : Promise.resolve(),
    ]).catch((error: unknown) => {
      logger.warn("Device groups refetch after a device group change failed", {
        companyId,
        ...apiErrorLogFields(error),
      });
    });
    refreshPortfolioItems(companyId);
  };

  // Not awaited: a large inventory shouldn't hold up the caller. A failed refresh keeps the loaded
  // items, old group names included, until their next load.
  const refreshPortfolioItems = (companyId: number): void => {
    const portfolioItemsStore = usePortfolioItemsStore();
    if (
      !portfolioItemsStore.allItems.some(
        (item) => item.company_id === companyId
      )
    ) {
      return;
    }
    portfolioItemsStore.refreshItems().catch((error: unknown) => {
      logger.warn(
        "Portfolio items refetch after a device group change failed",
        { companyId, ...apiErrorLogFields(error) }
      );
    });
  };

  const create = async (
    companyId: number,
    request: DeviceGroupCreateRequest
  ): Promise<DeviceGroup> => {
    const response = await deviceGroupsApi.create(companyId, request);
    await refreshCompany(companyId);
    return response.data;
  };

  /** Saves a rename and member changes in one request; a refusal leaves the group as it was. */
  const update = async (
    companyId: number,
    groupId: number,
    request: DeviceGroupUpdateRequest
  ): Promise<void> => {
    await deviceGroupsApi.update(companyId, groupId, request);
    await refreshCompany(companyId);
  };

  const remove = async (companyId: number, groupId: number): Promise<void> => {
    await deviceGroupsApi.remove(companyId, groupId);
    await refreshCompany(companyId);
  };

  return { groupsFor, fetchGroups, create, update, remove };
});
